/* eslint-disable no-unused-vars */
import { useInterval } from 'ahooks';
import React, { createContext, FC, ReactNode, useReducer } from 'react';

import { SubscriptionMsg, UpdateTopicMsg } from 'src/types';

interface WebSocketProviderProps {
    children: ReactNode;
}
export interface SubscriptionsData {
    [id: string]: SubscriptionMsg;
}
export interface SubscriptionsMsg {
    [id: string]: UpdateTopicMsg;
}

interface SocketData {
    socket: WebSocket;
    subscriptions: SubscriptionsData;
}

interface SocketTopicsData {
    [socketType: string]: SocketData;
}

export interface SocketContextType {
    sockets: SocketTopicsData;
    addSocket: (socketType: string, socket: WebSocket, subscriptions: SubscriptionsData) => void;
    removeSocket: (socketType: string) => void;
    getSocket: (socketType: string) => SocketData | void;
    updateSubscriptions: (socketType: string, subscriptions: SubscriptionsData) => void;
    removeAllSockets: () => void;
    reSubscribeTopic: (socketType: string, updatedMessage: SubscriptionsMsg) => void;
    closeSocketConnection: (socketType: string) => void;
    subscribeTopic: (socketType: string, messages: SubscriptionsMsg) => void;
    cancelTopicSubscription: (socketType: string, topics: string[]) => void;
}

const initialState: SocketTopicsData = {};

const reducer = (
    state: SocketTopicsData,
    action: { type: string; payload?: any },
): SocketTopicsData => {
    switch (action.type) {
        case 'ADD_SOCKET': {
            const { socketType, socket, subscriptions } = action.payload;
            return { ...state, [socketType]: { socket, subscriptions } };
        }

        case 'REMOVE_SOCKET': {
            const { [action.payload]: _, ...updatedSockets } = state;
            return updatedSockets;
        }

        case 'UPDATE_SUBSCRIPTIONS': {
            const { socketType: updateType, subscriptions: subscriptions } = action.payload;
            return {
                ...state,
                [updateType]: {
                    ...state[updateType],
                    subscriptions: subscriptions,
                },
            };
        }
        case 'REMOVE_ALL_SOCKETS':
            Object.values(state).forEach((socketObj) => {
                const { socket, subscriptions } = socketObj;
                if (socket?.readyState === WebSocket.OPEN) {
                    // cancel all the subscriptions before close
                    for (const msg of Object.values(subscriptions)) {
                        msg.event = 'cancel';
                        socket.send(JSON.stringify(msg));
                    }
                    socket.close();
                }
            });
            return {};
        case 'RESUBSCRIBE_TOPIC': {
            const { socketType, updatedMessages } = action.payload;
            const socketData = state[socketType];
            if (!socketData) {
                return state;
            }
            const { socket, subscriptions } = socketData;
            Object.keys(updatedMessages).map((topicId) => {
                let prvTopicId = topicId;
                const topicMessage = Object.values(subscriptions).find((msg) => {
                    if (msg.topic.startsWith(topicId)) {
                        prvTopicId = msg.topic;
                        return msg;
                    }
                });
                if (topicMessage) {
                    const newSubNeedsToAdd = {
                        ...topicMessage,
                        ...updatedMessages[topicId],
                        params: {
                            ...topicMessage.params,
                            ...updatedMessages[topicId].params,
                        },
                    };
                    topicMessage.event = 'cancel';
                    socket.send(JSON.stringify(topicMessage));
                    socket.send(JSON.stringify(newSubNeedsToAdd));
                    Object.keys(subscriptions).forEach((subTopic) => {
                        if (subscriptions[subTopic].topic === prvTopicId) {
                            delete subscriptions[subTopic];
                        }
                    });
                    subscriptions[newSubNeedsToAdd.topic] = newSubNeedsToAdd;
                }
            });
            return {
                ...state,
                [socketType]: {
                    ...state[socketType],
                    subscriptions: subscriptions,
                },
            };
        }
        case 'CLOSE_SOCKET_CONNECTION': {
            const { socketType } = action.payload;
            const socketData = state[socketType];
            if (!socketData) {
                return state;
            }
            const { socket, subscriptions } = socketData;
            if (socket?.readyState === WebSocket.OPEN) {
                Object.keys(subscriptions).map((topic: string) => {
                    const msg = subscriptions[topic];
                    msg.event = 'cancel';
                    socket.send(JSON.stringify(msg));
                });
                socket.close();
            }
            delete state[socketType];
            return state;
        }
        case 'SUBSCRIBE_TOPIC': {
            const { socketType, messages } = action.payload;
            const socketData = state[socketType];
            if (!socketData) {
                return state;
            }
            const { socket, subscriptions } = socketData;
            (Object.values(messages) as SubscriptionMsg[]).map((msg) => {
                socket.send(JSON.stringify(msg));
                subscriptions[msg.id] = msg;
            });
            return {
                ...state,
                [socketType]: {
                    ...state[socketType],
                    subscriptions: subscriptions,
                },
            };
        }
        case 'CANCEL_TOPIC_SUBSCRIPTION': {
            const { socketType, topics } = action.payload;
            const socketData = state[socketType];
            if (!socketData) {
                return state;
            }
            const { socket, subscriptions } = socketData;
            topics.map((topic: string) => {
                const topicMessage = Object.values(subscriptions).find((msg) => {
                    if (msg.topic.startsWith(topic)) {
                        return msg;
                    }
                });
                if (topicMessage) {
                    topicMessage.event = 'cancel';
                    socket.send(JSON.stringify(topicMessage));
                    delete subscriptions[topicMessage.id];
                }
            });
            return {
                ...state,
                [socketType]: {
                    socket: socket,
                    subscriptions: subscriptions,
                },
            };
        }
        default:
            return state;
    }
};

const SocketContext = createContext<SocketContextType>({
    sockets: {},
    addSocket: () => {},
    removeSocket: () => {},
    getSocket: () => {},
    updateSubscriptions: () => {},
    removeAllSockets: () => {},
    reSubscribeTopic: () => {},
    closeSocketConnection: () => {},
    subscribeTopic: () => {},
    cancelTopicSubscription: () => {},
});

export const WebSocketProvider: FC<WebSocketProviderProps> = ({ children }) => {
    const [sockets, dispatch] = useReducer(reducer, initialState);

    useInterval(() => {
        // Send a ping message every 5 seconds to keep the connection alive
        Object.values(sockets).map((socketObj) => {
            socketObj.socket?.readyState === WebSocket.OPEN &&
                socketObj.socket.send(JSON.stringify({ ping: new Date().getTime() }));
        });
    }, 5000);
    const addSocket = (socketType: string, socket: WebSocket, subscriptions: SubscriptionsData) => {
        dispatch({ type: 'ADD_SOCKET', payload: { socketType, socket, subscriptions } });
    };

    const removeSocket = (socketType: string) => {
        dispatch({ type: 'REMOVE_SOCKET', payload: socketType });
    };

    const getSocket = (socketType: string): SocketData => {
        return sockets[socketType];
    };

    const updateSubscriptions = (socketType: string, subscriptions: SubscriptionsData) => {
        dispatch({ type: 'UPDATE_SUBSCRIPTIONS', payload: { socketType, subscriptions } });
    };

    const removeAllSockets = () => {
        dispatch({ type: 'REMOVE_ALL_SOCKETS' });
    };
    const reSubscribeTopic = (socketType: string, updatedMessages: SubscriptionsMsg) => {
        dispatch({
            type: 'RESUBSCRIBE_TOPIC',
            payload: { socketType, updatedMessages },
        });
    };
    const closeSocketConnection = (socketType: string) => {
        dispatch({
            type: 'CLOSE_SOCKET_CONNECTION',
            payload: { socketType },
        });
    };
    const subscribeTopic = (socketType: string, messages: SubscriptionsMsg) => {
        dispatch({
            type: 'SUBSCRIBE_TOPIC',
            payload: { socketType, messages },
        });
    };
    const cancelTopicSubscription = (socketType: string, topics: string[]) => {
        dispatch({
            type: 'CANCEL_TOPIC_SUBSCRIPTION',
            payload: { socketType, topics },
        });
    };

    return (
        <SocketContext.Provider
            value={{
                sockets,
                addSocket,
                removeSocket,
                getSocket,
                updateSubscriptions,
                removeAllSockets,
                reSubscribeTopic,
                closeSocketConnection,
                subscribeTopic,
                cancelTopicSubscription,
            }}
        >
            {children}
        </SocketContext.Provider>
    );
};

export default SocketContext;
